import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  AccountSchema,
  OperationalStateSchema,
  OrganizationSchema,
  RecoverabilitySchema,
  RegistrationStatusSchema,
  ReturnCadenceSchema,
  TaxAccountMappingSchema,
  TaxCodeSchema,
  TaxDeterminationRuleSchema,
  TaxDirectionSchema,
  TaxMappingPurposeSchema,
  TaxRateSchema,
  TaxTreatmentSchema,
  VatRegistrationPeriodSchema,
  type TaxCodeDto,
  DocumentTypeSchema,
  RegistrationTransitionSchema,
  RegimeActivationSchema,
  type ClientDto,
  type SupplierDto,
  type TaxDeterminationRuleDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ActiveClientPicker } from '../crm/common';
import { SupplierPicker } from '../finance/lookups';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

const TAX = '/api/v1/tax';
const plain = (value: string) => value.replace(/_/g, ' ');

/**
 * The VAT configuration (FRD §6; SDD §8): registration periods, tax codes and their rates,
 * determination rules and tax account mappings, with the create and status-change operations the
 * existing API offers. Every value is Finance configuration: this screen defaults nothing, shows a
 * rate only as the API's decimal string, and leaves every rule of validity to the API.
 */
export function TaxConfigurationPage() {
  const session = useSession();
  const tabs = [
    {
      key: 'periods',
      label: 'Registration periods',
      capability: 'tax.vat_registration_period:view',
    },
    { key: 'codes', label: 'Tax codes', capability: 'tax.tax_code:view' },
    { key: 'rules', label: 'Determination rules', capability: 'tax.tax_determination_rule:view' },
    { key: 'mappings', label: 'Account mappings', capability: 'tax.tax_account_mapping:view' },
  ].filter((tab) => hasCapability(session.data, tab.capability));
  const [selected, setSelected] = useState(0);
  const index = Math.min(selected, tabs.length - 1);
  const current = tabs[index];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Tax configuration"
        description="VAT registration, tax codes, rates and determination, as configured by Finance."
      />
      <Tabs
        value={index}
        onChange={(_, value: number) => {
          setSelected(value);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        aria-label="Tax configuration"
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>
      <Section>
        {current?.key === 'periods' && <RegistrationPeriods />}
        {current?.key === 'codes' && <TaxCodes />}
        {current?.key === 'rules' && <DeterminationRules />}
        {current?.key === 'mappings' && <AccountMappings />}
      </Section>
    </Stack>
  );
}

function Toolbar({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ px: 2, pt: 2, justifyContent: 'flex-end' }}>
      {children}
    </Stack>
  );
}

function ListFrame<T>({
  query,
  label,
  empty,
  head,
  row,
}: {
  query: UseQueryResult<{ items: T[]; total: number }>;
  label: string;
  empty: string;
  head: readonly string[];
  row: (item: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingState label={`Loading ${label.toLowerCase()}`} />;
  if (query.isError) {
    return (
      <Box sx={{ p: 2 }}>
        <ErrorState message={query.error.message} />
      </Box>
    );
  }
  if (query.data.items.length === 0) return <EmptyState title={empty} />;
  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          {head.map((h) => (
            <TableCell key={h}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>{query.data.items.map(row)}</TableBody>
    </Table>
  );
}

/** Activate or deactivate a record with the version the list issued (If-Match, SDD §7.3). */
function useStatusToggle(path: (id: string) => string, schema: z.ZodType, invalidate: unknown[]) {
  const session = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (record: { id: string; status: string; version: number }) =>
      patchJson(
        path(record.id),
        { status: record.status === 'active' ? 'inactive' : 'active' },
        schema,
        session.data?.csrfToken ?? '',
        record.version,
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: invalidate });
    },
  });
}

function useCodes(enabled = true) {
  return useQuery({
    queryKey: ['tax', 'codes'],
    enabled,
    queryFn: () => getPage(`${TAX}/tax-codes?pageSize=200`, TaxCodeSchema),
  });
}

function usePostingAccounts(enabled: boolean) {
  return useQuery({
    queryKey: ['accounts', 'posting'],
    enabled,
    queryFn: () => getPage('/api/v1/ledger/accounts?pageSize=200', AccountSchema),
  });
}

function Select({
  label,
  value,
  onChange,
  options,
  required = false,
  helperText,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly (readonly [string, string])[];
  required?: boolean;
  helperText?: string;
}) {
  return (
    <TextField
      select
      fullWidth
      label={label}
      required={required}
      value={value}
      helperText={helperText}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      {!required && <MenuItem value="">—</MenuItem>}
      {options.map(([v, l]) => (
        <MenuItem key={v} value={v}>
          {l}
        </MenuItem>
      ))}
    </TextField>
  );
}

const enumOptions = (values: readonly string[]) => values.map((v) => [v, plain(v)] as const);

// ------------------------------------------------------------------ registration periods

function RegistrationPeriods() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'tax.vat_registration_period:create');
  const canRequestTransition = hasCapability(
    session.data,
    'tax.vat_registration_transition:create',
  );
  const [open, setOpen] = useState(false);
  const [transitionOpen, setTransitionOpen] = useState(false);
  // One legal entity (D2): the periods listed are the ones of the organization that exists.
  const organization = useQuery({
    queryKey: ['organization'],
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });
  const organizationId = organization.data?.id;
  const query = useQuery({
    queryKey: ['tax', 'registration-periods', organizationId],
    enabled: organizationId !== undefined,
    queryFn: () =>
      getPage(
        `${TAX}/registration-periods?organizationId=${organizationId ?? ''}`,
        VatRegistrationPeriodSchema,
      ),
  });
  return (
    <>
      {(canCreate || canRequestTransition) && (
        <Toolbar>
          {canCreate && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setOpen(true);
              }}
            >
              Open registration period
            </Button>
          )}
          {canRequestTransition && (
            <Button
              size="small"
              onClick={() => {
                setTransitionOpen(true);
              }}
            >
              Request registration
            </Button>
          )}
        </Toolbar>
      )}
      {organization.isError ? (
        <ErrorState message={`The legal entity could not be read: ${organization.error.message}`} />
      ) : (
        <ListFrame
          query={query}
          label="Registration periods"
          empty="No VAT registration period is configured"
          head={[
            'Registration',
            'Operational state',
            'TRN',
            'Return cadence',
            'Effective from',
            'Effective to',
          ]}
          row={(p) => (
            <TableRow key={p.id}>
              <TableCell sx={{ fontWeight: 600 }}>{plain(p.registrationStatus)}</TableCell>
              <TableCell>{plain(p.operationalState)}</TableCell>
              <TableCell>{p.trn ?? '—'}</TableCell>
              <TableCell>{p.returnCadence ?? '—'}</TableCell>
              <TableCell>{p.effectiveFrom}</TableCell>
              <TableCell>{p.effectiveTo ?? 'Open'}</TableCell>
            </TableRow>
          )}
        />
      )}
      {open && (
        <CreatePeriodDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
      {organizationId !== undefined && <RegistrationTransitions organizationId={organizationId} />}
      <RegimeActivations />
      {transitionOpen && organizationId !== undefined && (
        <RequestTransitionDialog
          organizationId={organizationId}
          onClose={() => {
            setTransitionOpen(false);
          }}
        />
      )}
    </>
  );
}

/**
 * The transitions to the registered state (FRD-VAT-012): each is approved through the configured
 * route before it changes anything; the approval inbox decides it.
 */
function RegistrationTransitions({ organizationId }: { organizationId: string }) {
  const session = useSession();
  const query = useQuery({
    queryKey: ['tax', 'registration-transitions', organizationId],
    enabled: hasCapability(session.data, 'tax.vat_registration_transition:view'),
    queryFn: () =>
      getPage(
        `${TAX}/registration-transitions?organizationId=${organizationId}`,
        RegistrationTransitionSchema,
      ),
  });
  if (!hasCapability(session.data, 'tax.vat_registration_transition:view')) return null;
  return (
    <ListFrame
      query={query}
      label="Registration transitions"
      empty="No transition to the registered state has been requested"
      head={['Registration date', 'TRN', 'Cadence', 'Reason', 'Status', 'Decided']}
      row={(t) => (
        <TableRow key={t.id}>
          <TableCell>{t.effectiveFrom}</TableCell>
          <TableCell>{t.trn}</TableCell>
          <TableCell>{t.returnCadence}</TableCell>
          <TableCell>{t.reason ?? '—'}</TableCell>
          <TableCell>
            <StatusChip status={t.status} />
          </TableCell>
          <TableCell>{t.decidedAt?.slice(0, 10) ?? '—'}</TableCell>
        </TableRow>
      )}
    />
  );
}

/**
 * Partial exemption and the Capital Assets Scheme stay dormant until activated (FRD-VAT-052 to
 * 054): an activation names the inactive accounts and tax codes Finance activates, is approved
 * through the configured route, and takes effect from its date.
 */
function RegimeActivations() {
  const session = useSession();
  const canView = hasCapability(session.data, 'tax.vat_regime_activation:view');
  const canRequest = hasCapability(session.data, 'tax.vat_regime_activation:create');
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ['tax', 'regime-activations'],
    enabled: canView,
    queryFn: () => getPage(`${TAX}/regime-activations`, RegimeActivationSchema),
  });
  if (!canView) return null;
  return (
    <>
      {canRequest && (
        <Toolbar>
          <Button
            size="small"
            onClick={() => {
              setOpen(true);
            }}
          >
            Request regime activation
          </Button>
        </Toolbar>
      )}
      <ListFrame
        query={query}
        label="VAT regime activations"
        empty="Partial exemption and the Capital Assets Scheme are not active"
        head={['Regime', 'Effective from', 'Accounts', 'Tax codes', 'Reason', 'Status']}
        row={(a) => (
          <TableRow key={a.id}>
            <TableCell>{plain(a.regime)}</TableCell>
            <TableCell>{a.effectiveFrom}</TableCell>
            <TableCell>{a.accountIds.length}</TableCell>
            <TableCell>{a.taxCodeIds.length}</TableCell>
            <TableCell>{a.reason}</TableCell>
            <TableCell>
              <StatusChip status={a.status} />
            </TableCell>
          </TableRow>
        )}
      />
      {open && (
        <RequestRegimeDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function RequestRegimeDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [regime, setRegime] = useState('');
  const [from, setFrom] = useState('');
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [codeIds, setCodeIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const accounts = useQuery({
    queryKey: ['accounts', 'inactive'],
    enabled: hasCapability(session.data, 'ledger.account:view'),
    queryFn: () =>
      getPage('/api/v1/ledger/accounts?pageSize=200&filter[status]=inactive', AccountSchema),
  });
  const codes = useCodes();
  const save = useMutation({
    mutationFn: () =>
      postJson(
        `${TAX}/regime-activations`,
        { regime, effectiveFrom: from, accountIds, taxCodeIds: codeIds, reason: reason.trim() },
        RegimeActivationSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tax', 'regime-activations'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Request a VAT regime activation"
      submitLabel="Submit for approval"
      canSubmit={
        regime !== '' &&
        from !== '' &&
        reason.trim().length >= 5 &&
        accountIds.length + codeIds.length > 0
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        select
        label="Regime"
        required
        value={regime}
        onChange={(e) => {
          setRegime(e.target.value);
        }}
      >
        <MenuItem value="partial_exemption">Partial exemption</MenuItem>
        <MenuItem value="capital_assets_scheme">Capital Assets Scheme</MenuItem>
      </TextField>
      <DateField label="Effective from" value={from} onChange={setFrom} required />
      <TextField
        select
        label="Inactive accounts to activate"
        value={accountIds}
        slotProps={{ select: { multiple: true } }}
        onChange={(e) => {
          setAccountIds(e.target.value as unknown as string[]);
        }}
      >
        {(accounts.data?.items ?? []).map((a) => (
          <MenuItem key={a.id} value={a.id}>
            {a.code} — {a.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Inactive tax codes to activate"
        value={codeIds}
        slotProps={{ select: { multiple: true } }}
        onChange={(e) => {
          setCodeIds(e.target.value as unknown as string[]);
        }}
      >
        {(codes.data?.items ?? [])
          .filter((c) => c.status === 'inactive')
          .map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.code} — {c.name}
            </MenuItem>
          ))}
      </TextField>
      <TextField
        label="Reason"
        required
        multiline
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
    </FormDialog>
  );
}

function RequestTransitionDialog({
  organizationId,
  onClose,
}: {
  organizationId: string;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState('');
  const [trn, setTrn] = useState('');
  const [cadence, setCadence] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      postJson(
        `${TAX}/registration-transitions`,
        {
          organizationId,
          effectiveFrom: from,
          trn: trn.trim(),
          returnCadence: cadence,
          ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
        },
        RegistrationTransitionSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tax', 'registration-transitions'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Request the transition to the registered state"
      submitLabel="Submit for approval"
      canSubmit={from !== '' && trn.trim() !== '' && cadence !== ''}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Typography variant="body2">
        Nothing changes until the configured approval route approves it; then the non-filing period
        closes the day before the registration date (FRD-VAT-012).
      </Typography>
      <DateField label="Registration date" value={from} onChange={setFrom} required />
      <TextField
        label="TRN"
        required
        value={trn}
        onChange={(e) => {
          setTrn(e.target.value);
        }}
      />
      <TextField
        select
        label="Return cadence"
        required
        value={cadence}
        onChange={(e) => {
          setCadence(e.target.value);
        }}
      >
        <MenuItem value="monthly">Monthly</MenuItem>
        <MenuItem value="quarterly">Quarterly</MenuItem>
      </TextField>
      <TextField
        label="Reason / evidence"
        multiline
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
    </FormDialog>
  );
}

/** A transition is a new period, never an edit (FRD-VAT-012); TRN and cadence are never defaulted. */
function CreatePeriodDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [state, setState] = useState('');
  const [trn, setTrn] = useState('');
  const [cadence, setCadence] = useState('');
  const [from, setFrom] = useState('');
  const organization = useQuery({
    queryKey: ['organization'],
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });
  const create = useMutation({
    mutationFn: () =>
      postJson(
        `${TAX}/registration-periods`,
        {
          organizationId: organization.data?.id ?? '',
          registrationStatus: status,
          operationalState: state,
          ...(trn.trim() !== '' ? { trn: trn.trim() } : {}),
          ...(cadence !== '' ? { returnCadence: cadence } : {}),
          effectiveFrom: from,
        },
        VatRegistrationPeriodSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['tax', 'registration-periods'] });
    },
  });
  return (
    <FormDialog
      open
      title="Open a VAT registration period"
      submitLabel="Open period"
      canSubmit={status !== '' && state !== '' && from !== '' && organization.data !== undefined}
      submitting={create.isPending}
      error={
        create.isError
          ? create.error.message
          : organization.isError
            ? organization.error.message
            : null
      }
      onClose={onClose}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <Select
        label="Registration status"
        required
        value={status}
        onChange={setStatus}
        options={enumOptions(RegistrationStatusSchema.options)}
      />
      <Select
        label="Operational state"
        required
        value={state}
        onChange={setState}
        options={enumOptions(OperationalStateSchema.options)}
      />
      <TextField
        label="TRN"
        value={trn}
        onChange={(e) => {
          setTrn(e.target.value);
        }}
        helperText="Supplied by Finance for a registered period."
      />
      <Select
        label="Return cadence"
        value={cadence}
        onChange={setCadence}
        options={enumOptions(ReturnCadenceSchema.options)}
      />
      <DateField label="Effective from" value={from} onChange={setFrom} required />
    </FormDialog>
  );
}

function DateField({
  label,
  value,
  onChange,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <TextField
      label={label}
      type="date"
      fullWidth
      required={required}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      slotProps={{ inputLabel: { shrink: true } }}
    />
  );
}

/**
 * Ends a rate or a rule from a date (FRD-VAT-001, VAT-016): the record keeps its history, and
 * what was already determined keeps the value it used.
 */
function EndDateDialog({
  title,
  path,
  body,
  version,
  schema,
  invalidate,
  onClose,
}: {
  title: string;
  path: string;
  body: (effectiveTo: string) => Record<string, unknown>;
  version: number;
  schema: z.ZodType;
  invalidate: unknown[];
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [date, setDate] = useState('');
  const save = useMutation({
    mutationFn: () => patchJson(path, body(date), schema, session.data?.csrfToken ?? '', version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invalidate });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={title}
      submitLabel="End"
      canSubmit={date !== ''}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <DateField label="Last effective day" value={date} onChange={setDate} required />
    </FormDialog>
  );
}

/** The document type input VAT under this code needs attached (FRD-VAT-036). */
function SupportingDocumentDialog({ code, onClose }: { code: TaxCodeDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [type, setType] = useState(code.supportingDocumentType ?? '');
  const types = useQuery({
    queryKey: ['document-types', 'finance.supplier_invoice'],
    enabled: hasCapability(session.data, 'platform.document_type:view'),
    queryFn: () =>
      getPage(
        '/api/v1/platform/document-types?pageSize=200&filter[applicable_record_type]=finance.supplier_invoice',
        DocumentTypeSchema,
      ),
  });
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${TAX}/tax-codes/${code.id}`,
        { status: code.status, supportingDocumentType: type === '' ? null : type },
        TaxCodeSchema,
        session.data?.csrfToken ?? '',
        code.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tax', 'codes'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={`Supporting document for ${code.code}`}
      submitLabel="Save"
      canSubmit
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Typography variant="body2">
        Input VAT under this code is recognised only while the invoice holds an available document
        of this type.
      </Typography>
      <TextField
        select
        fullWidth
        label="Document type"
        value={type}
        helperText={
          types.isError ? 'Needs permission to view document types' : 'None: no document required'
        }
        onChange={(e) => {
          setType(e.target.value);
        }}
      >
        <MenuItem value="">None</MenuItem>
        {(types.data?.items ?? []).map((t) => (
          <MenuItem key={t.code} value={t.code}>
            {t.name}
          </MenuItem>
        ))}
        {type !== '' && !(types.data?.items ?? []).some((t) => t.code === type) && (
          <MenuItem value={type}>{type}</MenuItem>
        )}
      </TextField>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------- tax codes

function TaxCodes() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'tax.tax_code:create');
  const canChange = hasCapability(session.data, 'tax.tax_code:change');
  const canAddRate = hasCapability(session.data, 'tax.tax_rate:create');
  const [open, setOpen] = useState(false);
  const [rateFor, setRateFor] = useState<TaxCodeDto | null>(null);
  const [documentFor, setDocumentFor] = useState<TaxCodeDto | null>(null);
  const query = useCodes();
  const toggle = useStatusToggle((id) => `${TAX}/tax-codes/${id}`, TaxCodeSchema, ['tax', 'codes']);
  const actions = canChange || canAddRate;
  return (
    <>
      {canCreate && (
        <Toolbar>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpen(true);
            }}
          >
            New tax code
          </Button>
        </Toolbar>
      )}
      {toggle.isError && (
        <Box sx={{ px: 2, pt: 2 }}>
          <ErrorState message={toggle.error.message} />
        </Box>
      )}
      <ListFrame
        query={query}
        label="Tax codes"
        empty="No tax code is configured"
        head={[
          'Code',
          'Name',
          'Treatment',
          'Recoverability',
          'Direction',
          'Rate',
          'Needs document',
          'Status',
          ...(actions ? ['Action'] : []),
        ]}
        row={(code) => (
          <TaxCodeRow
            key={code.id}
            code={code}
            onDocument={
              canChange
                ? () => {
                    setDocumentFor(code);
                  }
                : undefined
            }
            onToggle={
              canChange
                ? () => {
                    toggle.mutate(code);
                  }
                : undefined
            }
            onAddRate={
              canAddRate
                ? () => {
                    setRateFor(code);
                  }
                : undefined
            }
            showActions={actions}
          />
        )}
      />
      {open && (
        <CreateCodeDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
      {documentFor !== null && (
        <SupportingDocumentDialog
          code={documentFor}
          onClose={() => {
            setDocumentFor(null);
          }}
        />
      )}
      {rateFor !== null && (
        <AddRateDialog
          code={rateFor}
          onClose={() => {
            setRateFor(null);
          }}
        />
      )}
    </>
  );
}

function TaxCodeRow({
  code,
  onToggle,
  onAddRate,
  onDocument,
  showActions,
}: {
  code: TaxCodeDto;
  onToggle: (() => void) | undefined;
  onAddRate: (() => void) | undefined;
  onDocument: (() => void) | undefined;
  showActions: boolean;
}) {
  const session = useSession();
  const [ending, setEnding] = useState(false);
  const canEndRate = hasCapability(session.data, 'tax.tax_rate:change');
  const rates = useQuery({
    queryKey: ['tax', 'codes', code.id, 'rates'],
    enabled: hasCapability(session.data, 'tax.tax_rate:view'),
    queryFn: () => getPage(`${TAX}/tax-codes/${code.id}/rates`, TaxRateSchema),
  });
  const current = rates.data?.items.find((rate) => rate.effectiveTo === null);
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{code.code}</TableCell>
      <TableCell>{code.name}</TableCell>
      <TableCell>{plain(code.treatment)}</TableCell>
      <TableCell>{plain(code.recoverability)}</TableCell>
      <TableCell>{code.direction}</TableCell>
      <TableCell>
        {current === undefined ? '—' : `${current.rate} % from ${current.effectiveFrom}`}
      </TableCell>
      <TableCell>{code.supportingDocumentType ?? '—'}</TableCell>
      <TableCell>
        <StatusChip status={code.status} />
      </TableCell>
      {showActions && (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {onAddRate !== undefined && (
            <Button size="small" onClick={onAddRate}>
              Add rate
            </Button>
          )}
          {canEndRate && current !== undefined && (
            <Button
              size="small"
              onClick={() => {
                setEnding(true);
              }}
            >
              End rate
            </Button>
          )}
          {onDocument !== undefined && (
            <Button size="small" onClick={onDocument}>
              Document
            </Button>
          )}
          {ending && current !== undefined && (
            <EndDateDialog
              title={`End the ${current.rate} % rate of ${code.code}`}
              path={`${TAX}/tax-codes/${code.id}/rates/${current.id}`}
              body={(effectiveTo) => ({ effectiveTo })}
              version={current.version}
              schema={TaxRateSchema}
              invalidate={['tax', 'codes']}
              onClose={() => {
                setEnding(false);
              }}
            />
          )}
          {onToggle !== undefined && (
            <Button size="small" onClick={onToggle}>
              {code.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function CreateCodeDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [treatment, setTreatment] = useState('');
  const [recoverability, setRecoverability] = useState('');
  const [direction, setDirection] = useState('');
  const [percentage, setPercentage] = useState('');
  const partial = recoverability === 'partially_recoverable';
  const create = useMutation({
    mutationFn: () =>
      postJson(
        `${TAX}/tax-codes`,
        {
          code: code.trim(),
          name: name.trim(),
          treatment,
          recoverability,
          direction,
          ...(partial ? { recoverablePercentage: percentage.trim() } : {}),
        },
        TaxCodeSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['tax', 'codes'] });
    },
  });
  return (
    <FormDialog
      open
      title="New tax code"
      submitLabel="Create tax code"
      canSubmit={
        [code, name, treatment, recoverability, direction].every((v) => v.trim() !== '') &&
        (!partial || /^\d{1,2}(\.\d{1,2})?$/.test(percentage.trim()))
      }
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Code"
        required
        value={code}
        onChange={(e) => {
          setCode(e.target.value.toUpperCase());
        }}
        helperText="Upper case with underscores, starting with a letter."
      />
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <Select
        label="Treatment"
        required
        value={treatment}
        onChange={setTreatment}
        options={enumOptions(TaxTreatmentSchema.options)}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Select
          label="Recoverability"
          required
          value={recoverability}
          onChange={setRecoverability}
          options={enumOptions(RecoverabilitySchema.options)}
        />
        <Select
          label="Direction"
          required
          value={direction}
          onChange={setDirection}
          options={enumOptions(TaxDirectionSchema.options)}
        />
      </Stack>
      {partial && (
        <TextField
          label="Recoverable percentage"
          required
          value={percentage}
          helperText="The share of the VAT that is recoverable (FRD-VAT-035); the rest is cost"
          onChange={(e) => {
            setPercentage(e.target.value);
          }}
        />
      )}
    </FormDialog>
  );
}

function AddRateDialog({ code, onClose }: { code: TaxCodeDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [rate, setRate] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const create = useMutation({
    mutationFn: () =>
      postJson(
        `${TAX}/tax-rates`,
        {
          taxCodeId: code.id,
          rate: rate.trim(),
          effectiveFrom: from,
          ...(to !== '' ? { effectiveTo: to } : {}),
        },
        TaxRateSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['tax', 'codes', code.id, 'rates'] });
    },
  });
  return (
    <FormDialog
      open
      title={`Add a rate to ${code.code}`}
      submitLabel="Add rate"
      canSubmit={rate.trim() !== '' && from !== ''}
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Rate (%)"
        required
        value={rate}
        onChange={(e) => {
          setRate(e.target.value);
        }}
        helperText="A percentage with up to 6 decimals, supplied by Finance. No value is suggested."
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <DateField label="Effective from" value={from} onChange={setFrom} required />
        <DateField label="Effective to" value={to} onChange={setTo} />
      </Stack>
    </FormDialog>
  );
}

// ------------------------------------------------------------------- determination rules

function DeterminationRules() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'tax.tax_determination_rule:create');
  const canChange = hasCapability(session.data, 'tax.tax_determination_rule:change');
  const [open, setOpen] = useState(false);
  const [endingRule, setEndingRule] = useState<TaxDeterminationRuleDto | null>(null);
  const query = useQuery({
    queryKey: ['tax', 'rules'],
    queryFn: () => getPage(`${TAX}/determination-rules`, TaxDeterminationRuleSchema),
  });
  const toggle = useStatusToggle(
    (id) => `${TAX}/determination-rules/${id}`,
    TaxDeterminationRuleSchema,
    ['tax', 'rules'],
  );
  return (
    <>
      {canCreate && (
        <Toolbar>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpen(true);
            }}
          >
            New rule
          </Button>
        </Toolbar>
      )}
      {toggle.isError && (
        <Box sx={{ px: 2, pt: 2 }}>
          <ErrorState message={toggle.error.message} />
        </Box>
      )}
      <ListFrame
        query={query}
        label="Determination rules"
        empty="No determination rule is configured"
        head={[
          'Priority',
          'Name',
          'Direction',
          'Conditions',
          'Tax code',
          'Effective',
          'Status',
          ...(canChange ? ['Action'] : []),
        ]}
        row={(rule) => (
          <TableRow key={rule.id}>
            <TableCell>{rule.priority}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{rule.name}</TableCell>
            <TableCell>{rule.direction}</TableCell>
            <TableCell sx={{ maxWidth: 280 }}>
              <Typography variant="body2">{describeCondition(rule.conditions)}</Typography>
            </TableCell>
            <TableCell>{rule.taxCode}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {rule.effectiveFrom} – {rule.effectiveTo ?? 'open'}
            </TableCell>
            <TableCell>
              <StatusChip status={rule.status} />
            </TableCell>
            {canChange && (
              <TableCell>
                <Button
                  size="small"
                  onClick={() => {
                    toggle.mutate(rule);
                  }}
                >
                  {rule.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEndingRule(rule);
                  }}
                >
                  End
                </Button>
              </TableCell>
            )}
          </TableRow>
        )}
      />
      {open && (
        <CreateRuleDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
      {endingRule !== null && (
        <EndDateDialog
          title={`End rule ${endingRule.name}`}
          path={`${TAX}/determination-rules/${endingRule.id}`}
          body={(effectiveTo) => ({ status: endingRule.status, effectiveTo })}
          version={endingRule.version}
          schema={TaxDeterminationRuleSchema}
          invalidate={['tax', 'rules']}
          onClose={() => {
            setEndingRule(null);
          }}
        />
      )}
    </>
  );
}

/**
 * The attributes a rule may match on, as the determination service supplies them (SDD §20.4).
 * `amount` is compared as a decimal string, `vatApplicable` as a boolean, the rest as text.
 */
const ATTRIBUTES = [
  ['vatApplicable', 'Account: VAT applicable', 'boolean'],
  ['vatTreatment', 'Account: VAT treatment (COA text)', 'text'],
  ['accountCode', 'Account code', 'text'],
  ['description', 'Line description', 'text'],
  ['amount', 'Net amount', 'decimal'],
  ['supplierId', 'Supplier', 'text'],
  ['clientId', 'Client', 'text'],
] as const;
type AttributeKind = (typeof ATTRIBUTES)[number][2];
const OPERATORS: Readonly<Record<AttributeKind, readonly (readonly [string, string])[]>> = {
  boolean: [
    ['eq', 'is'],
    ['ne', 'is not'],
  ],
  text: [
    ['eq', 'equals'],
    ['ne', 'does not equal'],
    ['in', 'is one of'],
    ['not_in', 'is not one of'],
    ['is_null', 'is empty'],
    ['is_not_null', 'is not empty'],
  ],
  decimal: [
    ['eq', '='],
    ['ne', '≠'],
    ['gt', '>'],
    ['gte', '≥'],
    ['lt', '<'],
    ['lte', '≤'],
  ],
};

interface TermDraft {
  attr: string;
  op: string;
  value: string;
}

const kindOf = (attr: string): AttributeKind => ATTRIBUTES.find(([a]) => a === attr)?.[2] ?? 'text';

/** The term exactly as the evaluator compares it: booleans, decimal strings, text, lists. */
function termOf(draft: TermDraft): Record<string, unknown> {
  if (draft.op === 'is_null' || draft.op === 'is_not_null')
    return { attr: draft.attr, op: draft.op };
  const kind = kindOf(draft.attr);
  if (kind === 'boolean') return { attr: draft.attr, op: draft.op, value: draft.value === 'true' };
  if (draft.op === 'in' || draft.op === 'not_in') {
    return {
      attr: draft.attr,
      op: draft.op,
      value: draft.value
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v !== ''),
    };
  }
  return { attr: draft.attr, op: draft.op, value: draft.value.trim() };
}

function describeCondition(condition: unknown): string {
  if (typeof condition !== 'object' || condition === null) return '—';
  const c = condition as Record<string, unknown>;
  if (Array.isArray(c.all)) return c.all.map(describeCondition).join(' and ');
  if (Array.isArray(c.any)) return c.any.map(describeCondition).join(' or ');
  const label = ATTRIBUTES.find(([a]) => a === c.attr)?.[1] ?? String(c.attr);
  const op = typeof c.op === 'string' ? c.op.replace(/_/g, ' ') : '';
  const value = c.value === undefined ? '' : ` ${JSON.stringify(c.value)}`;
  return `${label} ${op}${value}`;
}

function CreateRuleDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const codes = useCodes();
  const [name, setName] = useState('');
  const [direction, setDirection] = useState('');
  const [priority, setPriority] = useState('');
  const [taxCodeId, setTaxCodeId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [combinator, setCombinator] = useState<'all' | 'any'>('all');
  const [terms, setTerms] = useState<TermDraft[]>([{ attr: '', op: '', value: '' }]);

  const complete = (t: TermDraft) =>
    t.attr !== '' &&
    t.op !== '' &&
    (t.op === 'is_null' || t.op === 'is_not_null' || t.value.trim() !== '');
  const conditions =
    terms.length === 1 && terms[0] !== undefined
      ? termOf(terms[0])
      : { [combinator]: terms.map(termOf) };

  const create = useMutation({
    mutationFn: () =>
      postJson(
        `${TAX}/determination-rules`,
        {
          name: name.trim(),
          direction,
          conditions,
          priority: Number(priority),
          taxCodeId,
          effectiveFrom: from,
          ...(to !== '' ? { effectiveTo: to } : {}),
        },
        TaxDeterminationRuleSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['tax', 'rules'] });
    },
  });

  const update = (index: number, patch: Partial<TermDraft>) => {
    setTerms((all) => all.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  return (
    <FormDialog
      open
      title="New determination rule"
      submitLabel="Create rule"
      canSubmit={
        name.trim() !== '' &&
        direction !== '' &&
        /^\d+$/.test(priority) &&
        taxCodeId !== '' &&
        from !== '' &&
        terms.every(complete)
      }
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Select
          label="Direction"
          required
          value={direction}
          onChange={setDirection}
          options={enumOptions(TaxDirectionSchema.options)}
        />
        <TextField
          label="Priority"
          required
          fullWidth
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value.replace(/\D/g, ''));
          }}
          helperText="Lowest number wins."
        />
      </Stack>
      <Select
        label="Tax code"
        required
        value={taxCodeId}
        onChange={setTaxCodeId}
        options={(codes.data?.items ?? []).map((c) => [c.id, `${c.code} — ${c.name}`] as const)}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <DateField label="Effective from" value={from} onChange={setFrom} required />
        <DateField label="Effective to" value={to} onChange={setTo} />
      </Stack>

      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Conditions</Typography>
          {terms.length > 1 && (
            <TextField
              select
              size="small"
              value={combinator}
              onChange={(e) => {
                setCombinator(e.target.value === 'any' ? 'any' : 'all');
              }}
              aria-label="Combine conditions"
            >
              <MenuItem value="all">all must match</MenuItem>
              <MenuItem value="any">any may match</MenuItem>
            </TextField>
          )}
        </Stack>
        <Stack spacing={1.5}>
          {terms.map((term, index) => {
            const kind = kindOf(term.attr);
            const noValue = term.op === 'is_null' || term.op === 'is_not_null';
            return (
              <Stack
                key={index}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <TextField
                  select
                  size="small"
                  label="Attribute"
                  value={term.attr}
                  sx={{ minWidth: 200 }}
                  onChange={(e) => {
                    update(index, { attr: e.target.value, op: '', value: '' });
                  }}
                >
                  {ATTRIBUTES.map(([a, l]) => (
                    <MenuItem key={a} value={a}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Test"
                  value={term.op}
                  disabled={term.attr === ''}
                  sx={{ minWidth: 140 }}
                  onChange={(e) => {
                    update(index, { op: e.target.value });
                  }}
                >
                  {OPERATORS[kind].map(([o, l]) => (
                    <MenuItem key={o} value={o}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
                {!noValue &&
                  (kind === 'boolean' ? (
                    <TextField
                      select
                      size="small"
                      label="Value"
                      value={term.value}
                      sx={{ minWidth: 110 }}
                      onChange={(e) => {
                        update(index, { value: e.target.value });
                      }}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  ) : (term.attr === 'supplierId' || term.attr === 'clientId') &&
                    (term.op === 'eq' || term.op === 'ne') ? (
                    // FRD-VAT-018: the counterparty is chosen from its master, never typed as an id.
                    <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                      <CounterpartyValue
                        kind={term.attr === 'supplierId' ? 'supplier' : 'client'}
                        id={term.value}
                        onChange={(id) => {
                          update(index, { value: id });
                        }}
                      />
                    </Box>
                  ) : (
                    <TextField
                      size="small"
                      label="Value"
                      value={term.value}
                      sx={{ flexGrow: 1 }}
                      onChange={(e) => {
                        update(index, { value: e.target.value });
                      }}
                      helperText={
                        term.op === 'in' || term.op === 'not_in'
                          ? 'Separate values with commas'
                          : undefined
                      }
                    />
                  ))}
                {terms.length > 1 && (
                  <IconButton
                    aria-label="Remove condition"
                    onClick={() => {
                      setTerms((all) => all.filter((_, i) => i !== index));
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            );
          })}
        </Stack>
        {terms.length < 20 && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => {
              setTerms((all) => [...all, { attr: '', op: '', value: '' }]);
            }}
          >
            Add condition
          </Button>
        )}
      </Box>
      <Alert severity="info" variant="outlined">
        The API validates the rule, including that no two active rules share a priority over the
        same period.
      </Alert>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------- account mappings

function AccountMappings() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'tax.tax_account_mapping:create');
  const canChange = hasCapability(session.data, 'tax.tax_account_mapping:change');
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ['tax', 'account-mappings'],
    queryFn: () => getPage(`${TAX}/account-mappings`, TaxAccountMappingSchema),
  });
  const toggle = useStatusToggle((id) => `${TAX}/account-mappings/${id}`, TaxAccountMappingSchema, [
    'tax',
    'account-mappings',
  ]);
  return (
    <>
      {canCreate && (
        <Toolbar>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpen(true);
            }}
          >
            New account mapping
          </Button>
        </Toolbar>
      )}
      {toggle.isError && (
        <Box sx={{ px: 2, pt: 2 }}>
          <ErrorState message={toggle.error.message} />
        </Box>
      )}
      <ListFrame
        query={query}
        label="Account mappings"
        empty="No tax account mapping is configured"
        head={[
          'Purpose',
          'Tax code',
          'Registration state',
          'Account',
          'Status',
          ...(canChange ? ['Action'] : []),
        ]}
        row={(m) => (
          <TableRow key={m.id}>
            <TableCell sx={{ fontWeight: 600 }}>{plain(m.mappingPurpose)}</TableCell>
            <TableCell>{m.taxCode ?? '—'}</TableCell>
            <TableCell>{m.registrationState === null ? '—' : plain(m.registrationState)}</TableCell>
            <TableCell>{m.accountCode}</TableCell>
            <TableCell>
              <StatusChip status={m.status} />
            </TableCell>
            {canChange && (
              <TableCell>
                <Button
                  size="small"
                  onClick={() => {
                    toggle.mutate(m);
                  }}
                >
                  {m.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
              </TableCell>
            )}
          </TableRow>
        )}
      />
      {open && (
        <CreateMappingDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

/** Tax code and registration state are optional: omitted, the mapping applies regardless. */
function CreateMappingDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const codes = useCodes(hasCapability(session.data, 'tax.tax_code:view'));
  const accounts = usePostingAccounts(true);
  const [purpose, setPurpose] = useState('');
  const [taxCodeId, setTaxCodeId] = useState('');
  const [registration, setRegistration] = useState('');
  const [accountId, setAccountId] = useState('');
  const create = useMutation({
    mutationFn: () =>
      postJson(
        `${TAX}/account-mappings`,
        {
          mappingPurpose: purpose,
          ...(taxCodeId !== '' ? { taxCodeId } : {}),
          ...(registration !== '' ? { registrationState: registration } : {}),
          accountId,
        },
        TaxAccountMappingSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['tax', 'account-mappings'] });
    },
  });
  return (
    <FormDialog
      open
      title="New tax account mapping"
      submitLabel="Create mapping"
      canSubmit={purpose !== '' && accountId !== ''}
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <Select
        label="Purpose"
        required
        value={purpose}
        onChange={setPurpose}
        options={enumOptions(TaxMappingPurposeSchema.options)}
      />
      <Select
        label="Tax code"
        value={taxCodeId}
        onChange={setTaxCodeId}
        options={(codes.data?.items ?? []).map((c) => [c.id, `${c.code} — ${c.name}`] as const)}
        helperText="Leave empty for a purpose-level mapping."
      />
      <Select
        label="Registration state"
        value={registration}
        onChange={setRegistration}
        options={enumOptions(RegistrationStatusSchema.options)}
        helperText="Leave empty when the mapping applies whatever the state."
      />
      <Select
        label="Account"
        required
        value={accountId}
        onChange={setAccountId}
        options={(accounts.data?.items ?? [])
          .filter((a) => a.isPosting)
          .map((a) => [a.id, `${a.code} — ${a.name}`] as const)}
      />
    </FormDialog>
  );
}

/** A supplier or client chosen by name or code for a rule condition (FRD-VAT-018). */
function CounterpartyValue({
  kind,
  id,
  onChange,
}: {
  kind: 'supplier' | 'client';
  id: string;
  onChange: (id: string) => void;
}) {
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  const [client, setClient] = useState<ClientDto | null>(null);
  const known = id === '' || (kind === 'supplier' ? supplier?.id : client?.id) === id;
  const helper = known ? undefined : `Currently #${id}`;
  return kind === 'supplier' ? (
    <SupplierPicker
      value={supplier}
      helperText={helper}
      onChange={(value) => {
        setSupplier(value);
        onChange(value?.id ?? '');
      }}
    />
  ) : (
    <ActiveClientPicker
      label="Client"
      value={client}
      helperText={helper}
      onChange={(value) => {
        setClient(value);
        onChange(value?.id ?? '');
      }}
    />
  );
}
